import { useCallback, useEffect, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { organizationsApi } from '@/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { MasterLocaleSelects } from '@/components/master/MasterLocaleSelects';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { useDebounce } from '@/hooks/useDebounce';
import { usePagination } from '@/hooks/usePagination';
import type { OrganizationRequest, OrganizationResponse } from '@/types/organization';
import { formatDate } from '@/utils/format';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

const emptyForm: OrganizationRequest = {
  name: '',
  email: '',
  phone: '',
  countryCode: 'IN',
  currencyCode: 'INR',
  timezone: 'Asia/Kolkata',
};

export function OrganizationsPage() {
  const pagination = usePagination();
  const debouncedSearch = useDebounce(pagination.search);
  const [data, setData] = useState<OrganizationResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<OrganizationResponse | null>(null);
  const [form, setForm] = useState<OrganizationRequest>(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await organizationsApi.list({
        page: pagination.page,
        size: pagination.size,
        search: debouncedSearch || undefined,
        activeOnly: false,
      });
      setData(res.content);
      pagination.applyPageResponse(res);
    } catch (err) {
      showApiError(err, 'Failed to load organizations');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.size, debouncedSearch, pagination]);

  useEffect(() => {
    load();
  }, [load]);

  const columns: Column<OrganizationResponse>[] = [
    { key: 'name', header: 'Name' },
    { key: 'email', header: 'Email' },
    { key: 'phone', header: 'Phone' },
    { key: 'subscriptionPlan', header: 'Plan' },
    {
      key: 'status',
      header: 'Status',
      render: (r) => (
        <span className={r.status ? 'text-green-600' : 'text-red-600'}>
          {r.status ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    { key: 'createdAt', header: 'Created', render: (r) => formatDate(r.createdAt) },
    {
      key: 'actions',
      header: '',
      render: (r) => (
        <button
          type="button"
          className="text-primary-600 hover:underline"
          onClick={() => {
            setEditing(r);
            setForm({
              name: r.name,
              email: r.email,
              phone: r.phone,
              gstNumber: r.gstNumber,
              address: r.address,
              city: r.city,
              state: r.state,
              country: r.country,
              countryCode: r.countryCode,
              currencyCode: r.currencyCode,
              pincode: r.pincode,
              timezone: r.timezone ?? 'Asia/Kolkata',
              subscriptionPlan: r.subscriptionPlan,
            });
            setModalOpen(true);
          }}
        >
          <EditIcon fontSize="small" />
        </button>
      ),
    },
  ];

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        await organizationsApi.update(editing.id, form);
        notify.success('Organization updated');
      } else {
        await organizationsApi.create(form);
        notify.success('Organization created');
      }
      setModalOpen(false);
      setEditing(null);
      setForm(emptyForm);
      load();
    } catch (err) {
      showApiError(err, 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Organizations"
        breadcrumbs={[{ label: 'Master', to: '/master' }, { label: 'Organizations' }]}
        actions={
          <Button
            onClick={() => {
              setEditing(null);
              setForm(emptyForm);
              setModalOpen(true);
            }}
          >
            <AddIcon fontSize="small" />
            Add Organization
          </Button>
        }
      />
      <DataTable columns={columns}
        data={data}
        keyField="id"
        loading={loading}
        search={pagination.search}
        onSearchChange={(v) => {
          pagination.setSearch(v);
          pagination.resetPage();
        }}
        page={pagination.page}
        totalPages={pagination.totalPages}
        totalElements={pagination.totalElements}
        onPageChange={pagination.setPage}
      />
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Edit Organization' : 'New Organization'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={save} loading={saving}>
              Save
            </Button>
          </>
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Input label="Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Input label="Email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <Input label="Phone" value={form.phone ?? ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          <Input label="GST" value={form.gstNumber ?? ''} onChange={(e) => setForm({ ...form, gstNumber: e.target.value })} />
          <Input label="City" value={form.city ?? ''} onChange={(e) => setForm({ ...form, city: e.target.value })} />
          <MasterLocaleSelects
            className="sm:col-span-2"
            value={{
              countryCode: form.countryCode ?? '',
              currencyCode: form.currencyCode ?? '',
              timezone: form.timezone ?? '',
            }}
            onChange={(patch) => setForm({ ...form, ...patch })}
            showCurrency
            countryRequired
            currencyRequired
            timezoneRequired
          />
        </div>
      </Modal>
    </div>
  );
}
